import React, { useState, useEffect } from "react";
import TripID from "../Trip Details/views/TripID";
import EscortDetailsHeader from "./views/EscortDetailsHeader";
import { useParams } from "react-router-dom";
import "./EscortDetails.css";
import EscortPassengersList from "./views/EscortPassengerList";
import EscortDepartureOfficersList from "./views/EscortDepartureOfficersList";
import VehicleList from "./views/VehicleList";
import EscortDepartureActionButtons from "./views/EscortDepartureActionButtons";
import EscortArrivalList from "./views/EscortArrivalList";
import EscortArrivalOfficersList from "./views/EscortArrivalOfficersList";
import { TRIP_PHASE, TRIP_STATUS } from "../../utils/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import Loader from "../../components/elements/Loader";
import useLandingAuthApi from "../../services/api/useLandingAuthApi";

const EscortDetails = () => {
  const { getTripById, addEscortOfficers } = useLandingAuthApi();

  const { tripId } = useParams();

  const { data: tripDetails, isLoading } = useQuery({
    queryKey: ["trip", tripId],
    queryFn: () => getTripById(tripId),
    refetchOnMount: "always",
  });
  const tripData = tripDetails?.data;

  const [arrivalOfficers, setArrivalOfficers] = useState([]);
  const [departureOfficers, setDepartureOfficers] = useState([]);

  const { mutate: addEscortOfficerMutate } = useMutation({
    mutationFn: ({ tripId, tripPhase, data }) =>
      addEscortOfficers(tripId, tripPhase, data),
    onSuccess: (response) => {},
    onError: (error) => {
      console.error("Error adding officer", error);
    },
  });

  useEffect(() => {
    if (tripData?.escortOfficers) {
      const filteredArrivalOfficers = tripData?.escortOfficers?.filter(
        (officer) => officer.tripPhase === TRIP_PHASE.ARRIVAL
      );

      const filteredDepartureOfficers = tripData?.escortOfficers?.filter(
        (officer) => officer.tripPhase === TRIP_PHASE.DEPARTURE
      );

      setArrivalOfficers(filteredArrivalOfficers);
      setDepartureOfficers(filteredDepartureOfficers);
    }
  }, [tripData?.escortOfficers]);

  const handleAddArrivalOfficer = (officers) => {
    const updatedOfficers = [...arrivalOfficers, ...officers];

    addEscortOfficerMutate({
      tripId: tripId,
      data: updatedOfficers,
      tripPhase: TRIP_PHASE.ARRIVAL,
    });
    setArrivalOfficers(updatedOfficers);
  };

  const handleAddDepartureOfficer = (officers) => {
    const updatedOfficers = [...departureOfficers, ...officers];

    addEscortOfficerMutate({
      tripId: tripId,
      data: updatedOfficers,
      tripPhase: TRIP_PHASE.DEPARTURE,
    });
    setDepartureOfficers(updatedOfficers);
  };

  const handleEditDepartureOfficer = (index, updatedOfficer) => {
    const updatedOfficers = [...departureOfficers];
    updatedOfficers[index] = updatedOfficer;

    const officersData = updatedOfficers.map((officer) => ({
      officerId: officer.officerId,
      tripPhase: TRIP_PHASE.DEPARTURE,
      hoursWorked: officer.hoursWorked,
    }));

    addEscortOfficerMutate({
      tripId: tripId,
      data: officersData,
      tripPhase: TRIP_PHASE.DEPARTURE,
    });
    setDepartureOfficers(updatedOfficers);
  };

  const handleEditArrivalOfficer = (index, updatedOfficer) => {
    const updatedOfficers = [...arrivalOfficers];
    updatedOfficers[index] = updatedOfficer;

    const officersData = updatedOfficers.map((officer) => ({
      officerId: officer.officerId,
      tripPhase: TRIP_PHASE.ARRIVAL,
      hoursWorked: officer.hoursWorked,
    }));

    addEscortOfficerMutate({
      tripId: tripId,
      data: officersData,
      tripPhase: TRIP_PHASE.ARRIVAL,
    });
    setArrivalOfficers(updatedOfficers);
  };

  const handleDeleteArrivalOfficer = (index) => {
    const updatedOfficers = arrivalOfficers.filter((_, i) => i !== index);
    setArrivalOfficers(updatedOfficers);

    const officersData = updatedOfficers.map((officer) => ({
      officerId: officer.officerId,
      tripPhase: TRIP_PHASE.ARRIVAL,
      hoursWorked: officer.hoursWorked,
    }));

    addEscortOfficerMutate({
      tripId: tripId,
      data: officersData,
      tripPhase: TRIP_PHASE.ARRIVAL,
    });
  };
  const handleDeleteDepartureOfficer = (index) => {
    const updatedOfficers = departureOfficers?.filter((_, i) => i !== index);
    setDepartureOfficers(updatedOfficers);

    const officersData = updatedOfficers?.map((officer) => ({
      officerId: officer.officerId,
      tripPhase: TRIP_PHASE.DEPARTURE,
      hoursWorked: officer.hoursWorked,
    }));

    addEscortOfficerMutate({
      tripId: tripId,
      data: officersData,
      tripPhase: TRIP_PHASE.DEPARTURE,
    });
  };
  const departureVehicles =
    tripData?.escortVehicles?.filter(
      (vehicle) => vehicle.tripPhase === TRIP_PHASE.DEPARTURE
    ) || [];
  const arrivalVehicles =
    tripData?.escortVehicles?.filter(
      (vehicle) => vehicle.tripPhase === TRIP_PHASE.ARRIVAL
    ) || [];

  if (isLoading) {
    return <Loader />;
  }
  const isCancelled = tripData?.status === TRIP_STATUS.CANCELLED;

  const hasPassengersRequiringEscort = tripData?.passengers?.some(
    (passenger) => passenger.requiresEscortService
  );

  return (
    <div>
      <div className="escort-details-container">
        <EscortDetailsHeader />

        <div className="escort-departure-airport-name">
          <div className="escort-details-airport-name-status-row">
            <span data-testid="departure-airport-name">{tripData?.departureAirport?.name} (Departure)</span>
            {isCancelled && (
              <div className="escort-details-cancelled-button" data-testid="departure-cancelled-status">
                <span>{tripData?.status}</span>
              </div>
            )}
          </div>
        </div>

        <div className="escort-details-row">
          <div className="escort-details-left-column">
            <TripID id={tripData?.trackingId} />

            <EscortPassengersList passengers={tripData?.passengers} />
            {(hasPassengersRequiringEscort ||
              departureVehicles?.length > 0) && (
              <EscortDepartureOfficersList
                officers={departureOfficers}
                onAddOfficer={handleAddDepartureOfficer}
                onEditOfficer={handleEditDepartureOfficer}
                onDeleteOfficer={handleDeleteDepartureOfficer}
                clearanceStatus={tripData?.escortDepartureStatus}
                airportId={tripData?.departureAirport?.id}
              />
            )}
          </div>
          <div className="escort-details-left-column">
            <div className="escort-departure-vehicle-list">
              <VehicleList vehicles={departureVehicles} />
            </div>
          </div>
        </div>
        {(hasPassengersRequiringEscort || departureVehicles?.length > 0) && (
          <EscortDepartureActionButtons
            tripId={tripId}
            trackingId={tripData?.trackingId}
            officers={departureOfficers}
            airportCodeOnApproved={tripData?.departureAirport}
            clearanceStatus={tripData?.escortDepartureStatus}
            correctionReason={tripData?.escortDepartureCorrectionReason}
          />
        )}
      </div>
      <div className="escort-arrival-airport-name">
        <div className="escort-details-airport-name-status-row">
          <span>{tripData?.arrivalAirport?.name} (Arrival)</span>
          {isCancelled && (
            <div className="escort-details-cancelled-button">
              <span>{tripData?.status}</span>
            </div>
          )}
        </div>
      </div>
      <div className="escort-details-row">
        <EscortArrivalList tripData={tripData} officers={arrivalOfficers} />
        {arrivalVehicles.length > 0 && (
          <EscortArrivalOfficersList
            officers={arrivalOfficers}
            onAddOfficer={handleAddArrivalOfficer}
            onEditOfficer={handleEditArrivalOfficer}
            onDeleteOfficer={handleDeleteArrivalOfficer}
            clearanceStatus={tripData?.escortArrivalStatus}
            airportId={tripData?.arrivalAirport?.id}
          />
        )}
      </div>
    </div>
  );
};

export default EscortDetails;
